import React, { useContext, useEffect, useState } from 'react';
import { Button, CircularProgress, makeStyles, Tab, Tabs } from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

import { catalogApi } from 'api/catalog';
import { productionApi as api } from 'api/production';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications, TabPanel } from 'shared';

import { CarouselItems } from './CarouselItems';
import { CarouselModal } from './CarouselModal';
import { PhotoModal } from './PhotoModal';

const useStyles = makeStyles({
    progress: {
        margin: '50px 0',
    },
});

function Carousels() {
    const classes = useStyles();
    const [week, setWeek] = useState('');
    const [year, setYear] = useState('');

    const [mostRecentPackGroups, setMostRecentPackGroups] = useState([]);

    const [packGroups, setPackGroups] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [selectedTab, setSelectedTab] = useState('');
    const [carouselModalOpen, setCarouselModalOpen] = useState(false);
    const [photoModalOpen, setPhotoModalOpen] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState({});
    const [brands, setBrands] = useState([]);
    const user = useContext(UserContext);

    const addItemToCarousel = async (carouselID, sellableItemID) => {
        const res = await api.addItemToCarousel(carouselID, sellableItemID);
        if (!res.status) {
            setNotification({ text: `Failed to add item to carousel. ${res.msg}`, level: 'error' });
            return false;
        }

        getData();
        return true;
    };

    const addBundleToCarousel = async (carouselID, sellableBundleID) => {
        const res = await api.addBundleToCarousel(carouselID, sellableBundleID);
        if (!res.status) {
            setNotification({ text: `Failed to add bundle to carousel. ${res.msg}`, level: 'error' });
            return false;
        }

        getData();
        return true;
    };

    const removeBundleFromCarousel = async (carouselID, sellableBundleID) => {
        const res = await api.removeBundleFromCarousel(carouselID, sellableBundleID);
        if (!res.status) {
            setNotification({ text: 'Failed to remove bundle from carousel', level: 'error' });
            return false;
        }

        getData();
        return true;
    };

    const removeItemFromCarousel = async (carouselID, sellableItemID) => {
        const res = await api.removeItemFromCarousel(carouselID, sellableItemID);
        if (!res.status) {
            setNotification({ text: 'Failed to remove item from carousel', level: 'error' });
            return false;
        }

        getData();
        return true;
    };

    const deleteCarousel = async (carouselID) => {
        const res = await api.deleteCarousel(carouselID);
        if (!res.status) {
            setNotification({ text: 'Failed to delete carousel', level: 'error' });
            return false;
        }

        getData();
        return true;
    };

    const createCarousel = async (formData) => {
        if (formData.packGroupIDs.length === 0) {
            formData.packGroupIDs = [packGroups[selectedTab].id];
        }
        const res = await api.createCarousel(formData);
        if (!res.status) {
            setNotification({ text: `Failed to create carousel. ${res.msg}`, level: 'error' });
            return false;
        }

        getData();
        return true;
    };

    let getData = async (newWeek, newYear) => {
        const res = await api.getPackGroups({
            facilityID: user.getFacilityID(),
            week: newWeek || week,
            year: newYear || year,
        });
        if (res.status === true) {
            const { data } = res;
            setWeek(data.week);
            setYear(data.year);

            const promises = data.packGroups.map(async (group) => {
                const carousels = await api.getCarousels(group.id);
                const sellableItems = await api.getSellableItems(group.id);
                const sellableBundles = await api.getSellableBundles(group.id);
                if (!carousels.status) {
                    setNotification({ text: 'Failed to get carousels', level: 'error' });
                    return group;
                }
                group.carousels = carousels.data.carousels.map((item) => {
                    item.numItems = item.items.length;
                    item.carouselType = item.type;
                    item.brands = item.brands.map((item) => ({ ...item, value: item.name, text: item.name }));
                    return item;
                });
                group.items = sellableItems.data.sellableItems;
                group.bundles = sellableBundles.data.sellableBundles;
                return group;
            });
            const packGroupArr = await Promise.all(promises);
            setPackGroups(data.packGroups || []);
            if (!selectedTab) {
                setSelectedTab(data.packGroups[0] ? '0' : '');
            }
        }
    };

    const getBrands = async () => {
        const res = await catalogApi.getBrands();
        if (!res.status) {
            setNotification({ text: 'Failed to get brands', level: 'error' });
            return;
        }
        setBrands(res.data.brands);
    };

    const getPackGroupsCurrentNext = async () => {
        const res = await api.getPackGroupsCurrentNext();
        if (!res.status) {
            setNotification({ text: 'Failed to get pack groups for current and next week', level: 'error' });
            return;
        }

        setMostRecentPackGroups(res.data);
    };

    useEffect(() => {
        getData();
        getBrands();
        getPackGroupsCurrentNext();
    }, []);

    const formFields = [
        {
            name: 'week',
            initialValue: week,
            inputElement: 'textField',
            gridValue: 5,
            label: 'Week',
            inputProps: { required: true, type: 'number', max: '53' },
        },
        {
            name: 'year',
            initialValue: year,
            inputElement: 'textField',
            gridValue: 5,
            label: 'Year',
            inputProps: { required: true, type: 'number', maxLength: '4' },
        },
    ];

    return (
        <div>
            <Notifications options={notification} />
            <h2>Carousels</h2>
            <PhotoModal
                open={photoModalOpen}
                selectedPhoto={selectedPhoto}
                setPhotoModalOpen={setPhotoModalOpen}
                updateItemPhoto={async (carouselID, key, file) => {
                    const res = await api.updateCarousel(carouselID, { [key]: file });
                    if (!res.status) {
                        setNotification({ text: `Failed to update item photo: ${res.msg}`, level: 'error' });
                    } else {
                        getData();
                    }
                    return res.status;
                }}
            />
            <CarouselModal
                brands={brands}
                open={carouselModalOpen}
                createCarousel={createCarousel}
                onClose={() => {
                    setCarouselModalOpen(false);
                }}
            />

            {week && year && (
                <FormComponent
                    formFields={formFields}
                    button={{ gridValue: 2, text: 'Set Week and Year' }}
                    onSubmit={async (formData) => {
                        getData(formData.week, formData.year);
                    }}
                />
            )}
            {selectedTab && (
                <div>
                    <Tabs
                        onChange={(event, value) => {
                            setSelectedTab(value);
                        }}
                        value={selectedTab}
                        indicatorColor="primary"
                        textColor="primary"
                        centered
                    >
                        {packGroups.map((group, index) => (
                            <Tab key={group.id} label={`Pack Group ${group.name}`} value={`${index}`} />
                        ))}
                    </Tabs>
                    {packGroups.map((group, packGroupIndex) => (
                        <TabPanel selectedTab={selectedTab} key={group.id} index={`${packGroupIndex}`}>
                            <DataTable
                                columns={[
                                    { Header: 'ID', accessor: 'id' },
                                    {
                                        Header: 'Rank Number',
                                        accessor: 'rankNumber',
                                        editable: true,
                                        editProps: { type: 'input', required: true },
                                    },
                                    { Header: 'Type', accessor: 'carouselType' },
                                    {
                                        Header: 'Name',
                                        accessor: 'name',
                                        editable: true,
                                        editProps: { type: 'input', required: true },
                                    },
                                    { Header: 'Num Items', accessor: 'numItems' },
                                    {
                                        Header: 'Featured',
                                        accessor: 'featured',
                                        type: 'checkbox',
                                        editable: true,
                                        customEdit: async (row) => {
                                            let confirmed = false;
                                            if (row.featured) {
                                                confirmed = window.confirm(
                                                    'Make featured carousel non featured? This will remove images, description, and other featured carousel data',
                                                );
                                            } else {
                                                confirmed = true;
                                            }

                                            if (confirmed) {
                                                const res = await api.updateCarousel(row.id, {
                                                    featured: !row.featured,
                                                });
                                                if (!res.status) {
                                                    setNotification({
                                                        text: `Failed to update carousel: ${res?.msg}`,
                                                        level: 'error',
                                                    });
                                                    return false;
                                                }
                                                getData();
                                                return res.status;
                                            }
                                        },
                                    },
                                    {
                                        Header: 'Large Image',
                                        accessor: 'photoUrlLarge',
                                        Cell: ({ row }) => {
                                            if (!row.original.featured) {
                                                return null;
                                            }
                                            return (
                                                <Button
                                                    id={`LargeImage_${row.id}_upload`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedPhoto({
                                                            item: row.original,
                                                            key: 'photoUrlLarge',
                                                        });
                                                        setPhotoModalOpen(true);
                                                    }}
                                                    style={{ margin: '3px', width: '100%' }}
                                                >
                                                    {!!row.original.photoUrlLarge && (
                                                        <img height="100" src={row.original.photoUrlLarge} />
                                                    )}
                                                    {!row.original.photoUrlLarge && <CloudUploadIcon />}
                                                </Button>
                                            );
                                        },
                                    },
                                    {
                                        Header: 'Medium Image',
                                        accessor: 'photoUrlMedium',
                                        Cell: ({ row }) => {
                                            if (!row.original.featured) {
                                                return null;
                                            }
                                            return (
                                                <Button
                                                    id={`MediumImage_${row.id}_upload`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedPhoto({
                                                            item: row.original,
                                                            key: 'photoUrlMedium',
                                                        });
                                                        setPhotoModalOpen(true);
                                                    }}
                                                    style={{ margin: '3px', width: '100%' }}
                                                >
                                                    {!!row.original.photoUrlMedium && (
                                                        <img height="100" src={row.original.photoUrlMedium} />
                                                    )}
                                                    {!row.original.photoUrlMedium && <CloudUploadIcon />}
                                                </Button>
                                            );
                                        },
                                    },
                                    {
                                        Header: 'Small Image',
                                        accessor: 'photoUrlSmall',
                                        Cell: ({ row }) => {
                                            if (!row.original.featured) {
                                                return null;
                                            }
                                            return (
                                                <Button
                                                    id={`SmallImage_${row.id}_upload`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedPhoto({
                                                            item: row.original,
                                                            key: 'photoUrlSmall',
                                                        });
                                                        setPhotoModalOpen(true);
                                                    }}
                                                    style={{ margin: '3px', width: '100%' }}
                                                >
                                                    {!!row.original.photoUrlSmall && (
                                                        <img height="100" src={row.original.photoUrlSmall} />
                                                    )}
                                                    {!row.original.photoUrlSmall && <CloudUploadIcon />}
                                                </Button>
                                            );
                                        },
                                    },
                                    {
                                        Header: 'Description',
                                        accessor: 'description',
                                        editable: (row) => row.original.featured,
                                        editProps: {
                                            type: 'input',
                                            multiline: true,
                                        },
                                    },
                                    {
                                        Header: 'Background Color',
                                        accessor: 'backgroundColor',
                                        cellStyle: (row) => {
                                            if (!row.backgroundColor) {
                                                return {};
                                            }
                                            return { backgroundColor: row.backgroundColor };
                                        },
                                        editable: (row) => row.original.featured,
                                    },
                                    {
                                        Header: 'Link URL',
                                        accessor: 'linkUrl',
                                        editable: true,
                                        editProps: { type: 'input', required: true },
                                    },
                                    {
                                        Header: 'Link Title',
                                        accessor: 'linkTitle',
                                        editable: true,
                                        editProps: { type: 'input', required: true },
                                    },
                                    {
                                        Header: 'Link Descriptor',
                                        accessor: 'linkDescriptor',
                                        editable: true,
                                        editProps: { type: 'input', required: true },
                                    },
                                    {
                                        Header: 'L1 Aisle ID',
                                        accessor: 'l1AisleID',
                                        editable: true,
                                        editProps: { type: 'input', required: false },
                                    },
                                    {
                                        Header: 'L2 Aisle ID',
                                        accessor: 'l2AisleID',
                                        editable: true,
                                        editProps: { type: 'input', required: false },
                                    },
                                    {
                                        Header: 'Delete Carousel',
                                        Cell: ({ row }) => (
                                            <Button
                                                onClick={async () => {
                                                    if (window.confirm('Delete Carousel?') === true) {
                                                        const carouselID = row.original.id;
                                                        const res = await deleteCarousel(carouselID);
                                                        return res;
                                                    }
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        ),
                                    },
                                ]}
                                expandable
                                editable
                                duplicateRowSettings={{
                                    enable: true,
                                    CustomFormComponent: (
                                        <CarouselModal
                                            createCarousel={createCarousel}
                                            mostRecentPackGroups={mostRecentPackGroups}
                                        />
                                    ),
                                    onSuccess: () => {
                                        getData();
                                        setNotification({ text: 'Carousel Created', level: 'success' });
                                    },
                                }}
                                ExpansionComponent={
                                    <CarouselItems
                                        setNotification={setNotification}
                                        addItemToCarousel={addItemToCarousel}
                                        addBundleToCarousel={addBundleToCarousel}
                                        removeItemFromCarousel={removeItemFromCarousel}
                                        removeBundleFromCarousel={removeBundleFromCarousel}
                                        sellableItems={group.items}
                                        sellableBundles={group.bundles}
                                        getData={getData}
                                    />
                                }
                                data={group.carousels || []}
                                saveEdit={async ({ field, id, row, value }) => {
                                    const res = await api.updateCarousel(id, { [field]: value });
                                    if (!res.status) {
                                        setNotification({
                                            text: `Failed to update carousel: ${res?.msg}`,
                                            level: 'error',
                                        });
                                        return false;
                                    }
                                    getData();
                                    return res.status;
                                }}
                            />

                            <Button
                                onClick={() => {
                                    setCarouselModalOpen(true);
                                }}
                            >
                                Add Carousel
                            </Button>
                        </TabPanel>
                    ))}
                </div>
            )}
        </div>
    );
}

export { Carousels };
